import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import SelectPaginate from 'app/shared/components/select-paginate/SelectPaginate';
import { BoxForm, FormControlDefault, TextFieldForms, TypographyForms } from 'app/shared/layout/register/DefaultFormsComponents';
import { RegisterButton } from 'app/shared/layout/register/RegisterButton';
import { RegisterLayout } from 'app/shared/layout/register/RegisterLayout';
import { RegisterTitle } from 'app/shared/layout/register/RegisterTitle';
import { TruckImplementListDTO } from 'app/shared/model/truck-implement.model';
import { Loading } from 'app/shared/util/loading';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { TruckGroupAlertSelectionModal } from './components/TruckGroupAlertSelectionModal';
import { TruckGroupTruckSelectionModal } from './components/TruckGroupTruckSelectionModal';
import { TruckGroupVirtualFenceSelectionModal } from './components/TruckGroupVirtualFenceSelectionModal';
import { createEntityWithDetails, getEntity, getGroupTruckIds, getGroupVirtualFenceIds, getGroupPolygonFenceIds, reset, updateEntityWithDetails, setGroupPolygonFences } from './truck-group.reducer';

const getCompanyFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  const id = params.get('companyId');
  const name = params.get('companyName');
  return id && name ? { id: Number(id), name } : null;
};

export const TruckGroupUpdate = (props: RouteComponentProps<{ id: string }>) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const truckGroupEntity = useAppSelector(state => state.truckGroup.entity);
  const loading = useAppSelector(state => state.truckGroup.loading);
  const updating = useAppSelector(state => state.truckGroup.updating);
  const updateSuccess = useAppSelector(state => state.truckGroup.updateSuccess);
  const groupTruckIds = useAppSelector(state => state.truckGroup.entitiesAlt2 as number[] | null);
  const groupVirtualFenceIds = useAppSelector(state => state.truckGroup.entitiesAlt as number[] | null);

  const [isNew] = useState(!props.match.params || !props.match.params.id);
  const [formData, setFormData] = useState({ id: null, name: '', description: '' });
  const [selectedCompany, setSelectedCompany] = useState<any>(() => getCompanyFromSearch(props.location.search));

  const [selectedAlerts, setSelectedAlerts] = useState<number[]>([]);
  const [initialAlerts, setInitialAlerts] = useState<number[] | null>(null);
  const [openAlertModal, setOpenAlertModal] = useState(false);

  const [selectedTrucks, setSelectedTrucks] = useState<number[]>([]);
  const [initialTrucks, setInitialTrucks] = useState<number[] | null>(null);
  const [truckListSnapshot, setTruckListSnapshot] = useState<TruckImplementListDTO[]>([]);
  const [openTruckModal, setOpenTruckModal] = useState(false);
  const [truckIdsRequested, setTruckIdsRequested] = useState(false);

  const [selectedVirtualFences, setSelectedVirtualFences] = useState<number[]>([]);
  const [initialVirtualFences, setInitialVirtualFences] = useState<number[] | null>(null);
  const [openVirtualFenceModal, setOpenVirtualFenceModal] = useState(false);
  const [virtualFenceIdsRequested, setVirtualFenceIdsRequested] = useState(false);

  const [selectedPolygonFences, setSelectedPolygonFences] = useState<number[]>([]);
  const [initialPolygonFences, setInitialPolygonFences] = useState<number[] | null>(null);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [trucksToMove, setTrucksToMove] = useState<TruckImplementListDTO[]>([]);
  const [trucksToMoveOut, setTrucksToMoveOut] = useState<TruckImplementListDTO[]>([]);

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(props.match.params.id));
    }
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (!isNew && truckGroupEntity && truckGroupEntity.id) {
      setFormData({
        id: truckGroupEntity.id,
        name: truckGroupEntity.name || '',
        description: truckGroupEntity.description || '',
      });

      if (isAdmin && truckGroupEntity.company?.id) {
        setSelectedCompany(prev => (prev?.id === truckGroupEntity.company.id ? prev : truckGroupEntity.company));
      }

      if (truckGroupEntity.alerts) {
        const alertIds = truckGroupEntity.alerts.map(a => a.id);
        setSelectedAlerts(alertIds);
        setInitialAlerts(alertIds);
      }

      dispatch(getGroupTruckIds(truckGroupEntity.id));
      setTruckIdsRequested(true);

      dispatch(getGroupVirtualFenceIds(truckGroupEntity.id));
      setVirtualFenceIdsRequested(true);

      dispatch(getGroupPolygonFenceIds(truckGroupEntity.id))
        .unwrap()
        .then((response: any) => {
          const ids: number[] = Array.isArray(response) ? response : (response?.data ?? []);
          setSelectedPolygonFences(ids);
          setInitialPolygonFences(ids);
        })
        .catch(() => {
          setSelectedPolygonFences([]);
          setInitialPolygonFences([]);
        });
    }
  }, [truckGroupEntity?.id]);

  useEffect(() => {
    if (truckIdsRequested && groupTruckIds !== null) {
      setSelectedTrucks(groupTruckIds);
      setInitialTrucks(groupTruckIds);
    }
  }, [groupTruckIds, truckIdsRequested]);

  useEffect(() => {
    if (virtualFenceIdsRequested && groupVirtualFenceIds !== null) {
      setSelectedVirtualFences(groupVirtualFenceIds);
      setInitialVirtualFences(groupVirtualFenceIds);
    }
  }, [groupVirtualFenceIds, virtualFenceIdsRequested]);

  const resolvedCompanyId = isAdmin ? selectedCompany?.id ?? truckGroupEntity?.company?.id ?? undefined : undefined;

  const handleClose = () => {
    if (isNew && isAdmin && selectedCompany?.id) {
      const params = new URLSearchParams();
      params.set('companyId', String(selectedCompany.id));
      params.set('companyName', selectedCompany.name ?? '');
      props.history.push(`/truck-group?${params.toString()}`);
    } else {
      props.history.push(`/truck-group${props.location.search}`);
    }
  };

  const handleInputChange = (fieldName: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleCompanyChange = (value: any) => {
    const companyChanged = selectedCompany?.id !== value?.id;
    setSelectedCompany(value);
    if (isNew && companyChanged) {
      setSelectedAlerts([]);
      setSelectedTrucks([]);
      setSelectedVirtualFences([]);
      setSelectedPolygonFences([]);
      setTruckListSnapshot([]);
    }
  };

  const doSave = async () => {
    const setsEqual = (a: number[], b: number[]) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

    const alertsChanged = isNew || initialAlerts === null || !setsEqual(selectedAlerts, initialAlerts);
    const trucksChanged = isNew || initialTrucks === null || !setsEqual(selectedTrucks, initialTrucks);
    const fencesChanged = isNew || initialVirtualFences === null || !setsEqual(selectedVirtualFences, initialVirtualFences);
    const polygonFencesChanged = isNew
      ? selectedPolygonFences.length > 0
      : initialPolygonFences === null || !setsEqual(selectedPolygonFences, initialPolygonFences);

    const dto = {
      ...(formData.id ? { id: formData.id } : {}),
      name: formData.name.trim(),
      description: formData.description,
      ...(isAdmin && isNew && selectedCompany ? { company: selectedCompany } : {}),
      alertIds: alertsChanged ? selectedAlerts : null,
      truckIds: trucksChanged ? selectedTrucks : null,
      virtualFenceIds: fencesChanged ? selectedVirtualFences : null,
    };

    let resultAction;
    if (isNew) {
      resultAction = await dispatch(createEntityWithDetails(dto));
    } else {
      resultAction = await dispatch(updateEntityWithDetails(dto));
    }

    if (polygonFencesChanged) {
      let savedGroupId: number | null = isNew ? null : (formData.id as number | null);

      if (isNew) {
        if (createEntityWithDetails.fulfilled.match(resultAction)) {
          savedGroupId = (resultAction.payload as any)?.data?.id ?? null;
        }
        if (!savedGroupId) return;
      }

      if (savedGroupId) {
        await dispatch(setGroupPolygonFences({ groupId: savedGroupId, polygonIds: selectedPolygonFences }));
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const conflictingIn: TruckImplementListDTO[] = [];
    const conflictingOut: TruckImplementListDTO[] = [];

    if (truckListSnapshot.length > 0) {
      truckListSnapshot.forEach(t => {
        if (selectedTrucks.includes(t.id) && t.truckGroupId != null && t.truckGroupId !== truckGroupEntity?.id) {
          conflictingIn.push(t);
        }
      });

      if (!isNew && truckGroupEntity?.id && !truckGroupEntity?.isDefault) {
        truckListSnapshot.forEach(t => {
          if (!selectedTrucks.includes(t.id) && t.truckGroupId === truckGroupEntity?.id) {
            conflictingOut.push(t);
          }
        });
      }
    }

    if (conflictingIn.length > 0 || conflictingOut.length > 0) {
      setTrucksToMove([...conflictingIn, ...conflictingOut]);
      setTrucksToMoveOut(conflictingOut);
      setConfirmDialogOpen(true);
      return;
    }

    await doSave();
  };

  const isCompanyLocked = isAdmin && isNew && !selectedCompany;

  return (
    <ThemeProvider theme={theme}>
      <RegisterLayout>
        <Box sx={{ width: { xs: '95%', sm: '70%', lg: '50%' } }} justifyContent="center">
          <RegisterTitle isNew={isNew} contentKey="fleetSenseApp.truckGroup" />
          <Box>
            {loading || updating ? (
              <Loading />
            ) : (
              <Box margin="0 auto" width="100%">
                <BoxForm onSubmit={handleSubmit}>
                  <FormControlDefault variant="outlined">
                    <TypographyForms>Nome</TypographyForms>
                    <TextFieldForms
                      value={formData.name}
                      id="truck-group-name"
                      name="name"
                      required
                      disabled={truckGroupEntity?.isDefault}
                      onChange={e => handleInputChange('name', e.target.value)}
                    />
                  </FormControlDefault>

                  <FormControlDefault variant="outlined">
                    <TypographyForms>Descrição</TypographyForms>
                    <TextFieldForms
                      value={formData.description}
                      id="truck-group-description"
                      name="description"
                      disabled={truckGroupEntity?.isDefault}
                      onChange={e => handleInputChange('description', e.target.value)}
                    />
                  </FormControlDefault>

                  {isAdmin && isNew && (
                    <FormControlDefault variant="outlined">
                      <SelectPaginate
                        label="Empresa"
                        apiUrl="api/companies/transporter"
                        onChange={handleCompanyChange}
                        value={selectedCompany}
                        valueName="name"
                        icon="sort-down"
                        isRequired={true}
                        filterStyle={true}
                        sortQuery="name"
                      />
                    </FormControlDefault>
                  )}

                  <FormControlDefault variant="outlined">
                    <TypographyForms>Ativos</TypographyForms>
                    <TextFieldForms
                      onClick={() => (isCompanyLocked ? null : setOpenTruckModal(true))}
                      value={
                        isCompanyLocked
                          ? 'Selecione uma empresa para ver os ativos'
                          : selectedTrucks.length > 0
                          ? `${selectedTrucks.length} ativo(s) selecionado(s)`
                          : 'Clique para selecionar ativos'
                      }
                      InputProps={{ readOnly: true }}
                      disabled={isCompanyLocked}
                      sx={{ cursor: isCompanyLocked ? 'not-allowed' : 'pointer' }}
                    />
                  </FormControlDefault>

                  <FormControlDefault variant="outlined">
                    <TypographyForms>Alertas de Sensores</TypographyForms>
                    <TextFieldForms
                      onClick={() => (isCompanyLocked ? null : setOpenAlertModal(true))}
                      value={
                        isCompanyLocked
                          ? 'Selecione uma empresa para ver os alertas'
                          : selectedAlerts.length > 0
                          ? `${selectedAlerts.length} alerta(s) selecionado(s)`
                          : 'Clique para selecionar alertas'
                      }
                      InputProps={{ readOnly: true }}
                      disabled={isCompanyLocked}
                      sx={{ cursor: isCompanyLocked ? 'not-allowed' : 'pointer' }}
                    />
                  </FormControlDefault>

                  <FormControlDefault variant="outlined">
                    <TypographyForms>Cercas Virtuais</TypographyForms>
                    <TextFieldForms
                      onClick={() => (isCompanyLocked ? null : setOpenVirtualFenceModal(true))}
                      value={
                        isCompanyLocked
                          ? 'Selecione uma empresa para ver as cercas'
                          : (selectedVirtualFences.length + selectedPolygonFences.length) > 0
                          ? `${selectedVirtualFences.length + selectedPolygonFences.length} cerca(s) selecionada(s)`
                          : 'Clique para selecionar cercas'
                      }
                      InputProps={{ readOnly: true }}
                      disabled={isCompanyLocked}
                      sx={{ cursor: isCompanyLocked ? 'not-allowed' : 'pointer' }}
                    />
                  </FormControlDefault>

                  <RegisterButton disabled={updating} />
                </BoxForm>
              </Box>
            )}
          </Box>
        </Box>
      </RegisterLayout>

      {openAlertModal && (
        <TruckGroupAlertSelectionModal
          open={openAlertModal}
          toggleModal={() => setOpenAlertModal(false)}
          initialSelectedAlerts={selectedAlerts}
          handleSave={alerts => setSelectedAlerts(alerts)}
          companyId={resolvedCompanyId}
        />
      )}

      {openTruckModal && (
        <TruckGroupTruckSelectionModal
          open={openTruckModal}
          toggleModal={() => setOpenTruckModal(false)}
          initialSelectedTrucks={selectedTrucks}
          handleSave={(trucks, snapshot) => {
            setSelectedTrucks(trucks);
            if (snapshot) setTruckListSnapshot(snapshot);
          }}
          currentGroupId={truckGroupEntity?.id}
          companyId={resolvedCompanyId}
          isDefaultGroup={truckGroupEntity?.isDefault === true}
        />
      )}

      {openVirtualFenceModal && (
        <TruckGroupVirtualFenceSelectionModal
          open={openVirtualFenceModal}
          toggleModal={() => setOpenVirtualFenceModal(false)}
          initialCircleFenceIds={selectedVirtualFences}
          initialPolygonFenceIds={selectedPolygonFences}
          handleSave={(circleIds, polygonIds) => {
            setSelectedVirtualFences(circleIds);
            setSelectedPolygonFences(polygonIds);
          }}
          companyId={resolvedCompanyId}
        />
      )}

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '15px', background: theme.palette.primary.main } }}
      >
        <DialogTitle>
          <Typography variant="h6">Confirmar movimentação de ativos</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {trucksToMove.filter(t => !trucksToMoveOut.includes(t)).length > 0 && (
            <>
              <Typography variant="body2" mb={1}>
                Os seguintes ativos pertencem a outro grupo e serão movidos para este grupo:
              </Typography>
              <List dense>
                {trucksToMove
                  .filter(t => !trucksToMoveOut.includes(t))
                  .map(truck => (
                    <ListItem key={`in-${truck.id}`} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={truck.licensePlate}
                        secondary={`Grupo atual: ${truck.truckGroupName}`}
                        secondaryTypographyProps={{ color: 'warning.main' }}
                      />
                    </ListItem>
                  ))}
              </List>
            </>
          )}
          {trucksToMoveOut.length > 0 && (
            <>
              <Typography variant="body2" mb={1} mt={trucksToMove.filter(t => !trucksToMoveOut.includes(t)).length > 0 ? 2 : 0}>
                Os seguintes ativos serão removidos deste grupo e movidos para o <strong>Grupo Geral</strong>:
              </Typography>
              <List dense>
                {trucksToMoveOut.map(truck => (
                  <ListItem key={`out-${truck.id}`} sx={{ py: 0.5 }}>
                    <ListItemText primary={truck.licensePlate} secondaryTypographyProps={{ color: 'info.main' }} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
          <Typography variant="body2" mt={2} color="text.secondary">
            Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '8px 24px', my: 1 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} disableRipple variant="text" sx={{ color: 'text.primary' }}>
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              setConfirmDialogOpen(false);
              await doSave();
            }}
            disableRipple
            variant="contained"
            color="secondary"
          >
            Confirmar e mover
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default TruckGroupUpdate;
